import { IUpdateCartItemRequestV2 } from '@/api/types';
import { getQueryClient } from '@/lib/getQueryClient';
import { request, requestV2 } from '@/lib/request';
import { useMutation, useQuery } from '@/lib/useWrappingQuery';
import type { QueryKey } from '@tanstack/react-query';
import { cartQueryOptions, ICartGroup } from './options';
import { calcGroupShippingFee } from './shipping';

interface UseCartGroupsOptions {
  enabled?: boolean;
  onError?: (error: unknown) => void;
  skipDefaultErrorHandling?: boolean;
}

interface UseCartCountOptions {
  enabled?: boolean;
  onError?: (error: unknown) => void;
  skipDefaultErrorHandling?: boolean;
}

const defaultCartGroupsOption = cartQueryOptions.groups({
  pathVariables: undefined,
  payload: undefined,
});

const isCartGroupsQueryKey = (queryKey: QueryKey) => {
  const [pathSegments] = queryKey;

  if (!Array.isArray(pathSegments) || pathSegments.length !== 2) {
    return false;
  }

  const [resourceSegment, cartSegment] = pathSegments;

  return (
    resourceSegment === 'users' &&
    typeof cartSegment === 'string' &&
    (cartSegment === 'cart' || cartSegment.startsWith('cart?'))
  );
};

const removeUnavailableItemsFromGroups = (
  groups: ICartGroup[],
  removedCartItemIds?: Set<string>
) =>
  groups
    .map(group => {
      const remainingItems = group.items.filter(it => {
        const isUnavailable = it.availability !== 'AVAILABLE';
        if (isUnavailable) {
          removedCartItemIds?.add(it.id);
        }
        return !isUnavailable;
      });
      const productAmount = remainingItems.reduce(
        (sum, it) => sum + it.totalPrice,
        0
      );
      const shippingFee = calcGroupShippingFee(remainingItems, productAmount, {
        isBundle: group.isBundleDeliveryGroup === true,
        bundleFreeThreshold: group.bundleFreeShippingThresholdAmount,
      });
      const totalAmount = productAmount + shippingFee;
      return {
        ...group,
        items: remainingItems,
        summary: {
          productAmount,
          shippingFee,
          totalAmount,
        },
      } as ICartGroup;
    })
    .filter(g => g.items.length > 0);

export const useCartGroupsQuery = (
  params?: { deliveryAddressId?: number },
  options?: UseCartGroupsOptions
) => {
  const option = params?.deliveryAddressId
    ? cartQueryOptions.groups({
        pathVariables: { deliveryAddressId: params.deliveryAddressId },
        payload: undefined,
      })
    : defaultCartGroupsOption;

  return useQuery<ICartGroup[]>({
    ...option,
    // refetch 시 이전 데이터를 유지하여 컴포넌트 재마운트/애니메이션 재실행을 방지
    placeholderData: prev => prev,
    // 결제 완료 후 히스토리백으로 장바구니에 복귀하면 서버 상태를 다시 확인해야 한다.
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    refetchOnMount: true,
    ...options,
  });
};

export const useCartCountQuery = (options?: UseCartCountOptions) => {
  return useQuery<number>({
    ...cartQueryOptions.count(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};

export const useAddCartItemsMutation = () => {
  const queryClient = getQueryClient();
  return useMutation<boolean, any, IUpdateCartItemRequestV2[]>({
    mutationFn: (payload: IUpdateCartItemRequestV2[]) =>
      requestV2.post<boolean>('/users/cart', payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: defaultCartGroupsOption.queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: cartQueryOptions.count().queryKey,
        }),
      ]);
    },
  });
};

export const useDeleteCartItemsMutation = (params?: {
  deliveryAddressId?: number;
}) => {
  const queryClient = getQueryClient();
  const groupsOption = params?.deliveryAddressId
    ? cartQueryOptions.groups({
        pathVariables: { deliveryAddressId: params.deliveryAddressId },
        payload: undefined,
      })
    : defaultCartGroupsOption;
  const groupsQueryKey = groupsOption.queryKey;

  return useMutation<
    boolean,
    any,
    number[],
    { previousGroups?: ICartGroup[]; previousCount?: number }
  >({
    mutationFn: (cartIds: number[]) =>
      request.delete<boolean>(`/users/cart/${cartIds.join(',')}`),
    onMutate: async (cartIds: number[]) => {
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: groupsQueryKey,
        }),
        queryClient.cancelQueries({
          queryKey: cartQueryOptions.count().queryKey,
        }),
      ]);

      const previousGroups =
        queryClient.getQueryData<ICartGroup[]>(groupsQueryKey);
      const previousCount = queryClient.getQueryData<number>(
        cartQueryOptions.count().queryKey
      );

      const nextGroups = (previousGroups ?? [])
        .map(group => {
          const remainingItems = group.items.filter(
            it => !cartIds.includes(Number(it.id))
          );
          const productAmount = remainingItems.reduce(
            (sum, it) => sum + it.totalPrice,
            0
          );
          const shippingFee = calcGroupShippingFee(
            remainingItems,
            productAmount,
            {
              isBundle: group.isBundleDeliveryGroup === true,
              bundleFreeThreshold: group.bundleFreeShippingThresholdAmount,
            }
          );
          const totalAmount = productAmount + shippingFee;
          return {
            ...group,
            items: remainingItems,
            summary: {
              productAmount,
              shippingFee,
              totalAmount,
            },
          } as ICartGroup;
        })
        .filter(g => g.items.length > 0);

      queryClient.setQueryData<ICartGroup[]>(groupsQueryKey, nextGroups);

      const removedCount = cartIds.length;
      queryClient.setQueryData<number>(cartQueryOptions.count().queryKey, old =>
        Math.max(0, (old ?? previousCount ?? 0) - removedCount)
      );

      return { previousGroups, previousCount };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx && ctx.previousGroups !== undefined) {
        queryClient.setQueryData(groupsQueryKey, ctx.previousGroups);
      }
      if (ctx && ctx.previousCount !== undefined) {
        queryClient.setQueryData(
          cartQueryOptions.count().queryKey,
          ctx.previousCount
        );
      }
    },
    onSuccess: async () => {
      // 낙관적 업데이트로 충분하므로 invalidateQueries 불필요 (삭제 버그 방지)
    },
  });
};

export const useDeleteUnavailableCartItemsMutation = () => {
  const queryClient = getQueryClient();

  return useMutation<
    boolean,
    any,
    void,
    {
      previousGroupsEntries?: Array<[QueryKey, ICartGroup[] | undefined]>;
      previousCount?: number;
    }
  >({
    mutationFn: () => request.delete<boolean>('/users/cart/not-available'),
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({
          predicate: query => isCartGroupsQueryKey(query.queryKey),
        }),
        queryClient.cancelQueries({
          queryKey: cartQueryOptions.count().queryKey,
        }),
      ]);

      const previousGroupsEntries = queryClient.getQueriesData<ICartGroup[]>({
        predicate: query => isCartGroupsQueryKey(query.queryKey),
      });
      const previousCount = queryClient.getQueryData<number>(
        cartQueryOptions.count().queryKey
      );

      const removedCartItemIds = new Set<string>();
      previousGroupsEntries.forEach(([queryKey, previousGroups]) => {
        const nextGroups = removeUnavailableItemsFromGroups(
          previousGroups ?? [],
          removedCartItemIds
        );
        queryClient.setQueryData<ICartGroup[]>(queryKey, nextGroups);
      });
      queryClient.setQueryData<number>(cartQueryOptions.count().queryKey, old =>
        Math.max(0, (old ?? previousCount ?? 0) - removedCartItemIds.size)
      );

      return { previousGroupsEntries, previousCount };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousGroupsEntries !== undefined) {
        ctx.previousGroupsEntries.forEach(([queryKey, previousGroups]) => {
          queryClient.setQueryData(queryKey, previousGroups);
        });
      }
      if (ctx?.previousCount !== undefined) {
        queryClient.setQueryData(
          cartQueryOptions.count().queryKey,
          ctx.previousCount
        );
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          predicate: query => isCartGroupsQueryKey(query.queryKey),
        }),
        queryClient.invalidateQueries({
          queryKey: cartQueryOptions.count().queryKey,
        }),
      ]);
    },
  });
};

export const useUpdateCartItemsMutation = (params?: {
  deliveryAddressId?: number;
  onError?: (error: any) => void;
  skipDefaultErrorHandling?: (error: any) => boolean;
}) => {
  const queryClient = getQueryClient();
  const groupsOption = params?.deliveryAddressId
    ? cartQueryOptions.groups({
        pathVariables: { deliveryAddressId: params.deliveryAddressId },
        payload: undefined,
      })
    : defaultCartGroupsOption;
  const groupsQueryKey = groupsOption.queryKey;

  return useMutation<boolean, any, IUpdateCartItemRequestV2[]>({
    mutationFn: (payload: IUpdateCartItemRequestV2[]) =>
      requestV2.post<boolean>('/users/cart', payload),
    skipDefaultErrorHandling: error =>
      params?.skipDefaultErrorHandling?.(error) ?? false,
    onSuccess: async () => {
      // 낙관적 업데이트로 UI가 이미 반영되어 있으므로 성공 시 리패치 불필요
      // InputStepper 조작 시 체크박스 번쩍거림 방지
    },
    onError: async error => {
      // 에러 시에만 리패치하여 서버 상태와 동기화
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: groupsQueryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: cartQueryOptions.count().queryKey,
        }),
      ]);
      params?.onError?.(error);
    },
  });
};
